import React from "react";
import { useTheme } from "../../context/ThemeContext";
import Typewriter from "typewriter-effect";
import Resume from "../../assets/docs/Ayush-Rai's-SDE-CV.pdf";
import { BsFillMoonStarsFill, BsFillSunFill } from "react-icons/bs";
import { FaWhatsapp, FaDownload, FaGithub, FaLinkedin, FaTwitter, FaLink  } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import Fade from "react-reveal/Fade";

const Home = () => {
  const [theme, setTheme] = useTheme();

  const handleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  const socialLinks = [
    { icon: FaGithub, url: "https://github.com/ayush123-bit", label: "GitHub" },
    { icon: FaLinkedin, url: "http://linkedin.com/in/ayush-rai-7109202b6", label: "LinkedIn" },
    { icon: FaTwitter, url: "https://x.com/AyushRa36652420", label: "Twitter" },
    { icon: FaLink , url: "https://leetcode.com/u/pepcoderayu", label: "Leetcode" },
  ];

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden
        bg-gradient-to-br from-blue-50 to-indigo-50
        dark:from-[#0a0a18] dark:to-[#0f0f1a]
        transition-colors duration-300"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-[#f29f67]/20 dark:bg-[#f29f67]/10 blur-[100px] animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-[#3b8ff3]/20 dark:bg-[#3b8ff3]/10 blur-[120px] animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-purple-500/5 dark:bg-purple-500/5 blur-[100px]" />
        
        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
        
        {/* Animated Lines */}
        <div className="absolute top-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#f29f67]/20 to-transparent animate-slide" />
        <div className="absolute bottom-1/3 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#3b8ff3]/20 to-transparent animate-slide-reverse" />
      </div>

      {/* Theme Toggle Button */}
      <button
        onClick={handleTheme}
        className="fixed right-6 top-6 z-50
          w-12 h-12 flex items-center justify-center
          bg-white/90 dark:bg-[#1a1a2e] 
          backdrop-blur-md
          border border-gray-200 dark:border-white/10
          rounded-full
          text-[#f29f67] hover:text-white
          hover:bg-[#f29f67] dark:hover:bg-[#f29f67]
          shadow-lg hover:shadow-xl
          transition-all duration-300
          group"
        aria-label="Toggle theme"
      >
        {theme === "light" ? (
          <BsFillMoonStarsFill size={18} className="group-hover:rotate-12 transition-transform" />
        ) : (
          <BsFillSunFill size={18} className="group-hover:rotate-90 transition-transform" />
        )}
      </button>

      {/* Main Content */}
      <div className="relative z-10 w-full px-6 py-24 md:py-0">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1">
              <Fade left>
                {/* Status Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6
                  bg-white/80 dark:bg-white/5 
                  backdrop-blur-sm
                  border border-gray-200 dark:border-white/10
                  shadow-sm">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    Open for opportunities
                  </span>
                </div>

                <p className="text-gray-600 dark:text-gray-400 text-lg font-light mb-2 tracking-wide">
                  Hi there 👋 I'm
                </p>

                <h1
                  className="text-5xl sm:text-6xl lg:text-7xl font-extrabold mb-4 leading-tight
                    text-gray-800 dark:text-white"
                  style={{ fontFamily: "'Syne', sans-serif" }}
                >
                  Ayush{" "}
                  <span
                    className="bg-gradient-to-r from-[#f29f67] to-[#3b8ff3] 
                      bg-clip-text text-transparent"
                  >
                    Rai
                  </span>
                </h1>

                <div className="text-2xl sm:text-3xl font-semibold text-gray-700 dark:text-gray-300 mb-6 h-12">
                  <Typewriter
                    options={{
                      strings: [
                        "FullStack Developer",
                        "MERN Stack Developer",
                        "React Native Developer",
                        "Backend Engineer",
                      ],
                      autoStart: true,
                      loop: true,
                      wrapperClassName: "text-[#f29f67]",
                      cursorClassName: "text-[#f29f67]",
                      delay: 50,
                      deleteSpeed: 30,
                    }}
                  />
                </div>

                <p className="text-gray-600 dark:text-gray-400 text-base leading-relaxed max-w-xl mb-8">
                  Building scalable, user-centric web applications with modern technologies.
                  Passionate about clean code, great UX, and impactful solutions that make a difference.
                </p>

                {/* Social Links */}
                <div className="flex gap-3 mb-8">
                  {socialLinks.map((social, idx) => (
                    <a
                      key={idx}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 flex items-center justify-center rounded-full
                        bg-white/80 dark:bg-white/5
                        border border-gray-200 dark:border-white/10
                        text-gray-600 dark:text-gray-400
                        hover:text-white hover:bg-[#f29f67] dark:hover:bg-[#f29f67]
                        hover:border-transparent
                        hover:-translate-y-1
                        transition-all duration-300"
                      aria-label={social.label}
                    >
                      <social.icon size={16} />
                    </a>
                  ))}
                </div>

                <Fade bottom>
                  <div className="flex flex-wrap gap-4">
                    <a
                      href="https://api.whatsapp.com/send?phone=918318542040"
                      rel="noreferrer"
                      target="_blank"
                      className="group flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm
                        bg-gradient-to-r from-[#f29f67] to-[#f29f67]/80
                        text-white 
                        hover:shadow-2xl hover:shadow-[#f29f67]/30 
                        hover:-translate-y-0.5
                        transition-all duration-300
                        relative overflow-hidden"
                    >
                      <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-[#f29f67]/0 to-white/20 transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></span>
                      <FaWhatsapp size={16} className="relative z-10" />
                      <span className="relative z-10">Hire Me</span>
                    </a>

                    <a
                      href={Resume}
                      download="Ayush-Rai-Resume.pdf"
                      className="group flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm
                        border-2 border-[#3b8ff3]
                        text-[#3b8ff3]
                        hover:bg-[#3b8ff3] hover:text-white
                        hover:shadow-2xl hover:shadow-[#3b8ff3]/30
                        hover:-translate-y-0.5
                        transition-all duration-300
                        relative overflow-hidden"
                    >
                      <FaDownload size={14} className="group-hover:animate-bounce transition-all" />
                      My Resume
                    </a>
                  </div>
                </Fade>
              </Fade>
            </div>

            {/* Right Content - Profile Image with Animation */}
            <div className="order-1 lg:order-2 flex justify-center">
              <Fade right>
                <div className="relative group">
                  {/* Animated Border */}
                  <div className="absolute -inset-3 rounded-3xl bg-gradient-to-r from-[#f29f67] via-[#3b8ff3] to-[#f29f67] opacity-60 blur-xl group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-spin-slow"></div>
                  
                  {/* Image Container */}
                  <div className="relative rounded-2xl overflow-hidden
                    bg-white/10 dark:bg-white/5
                    backdrop-blur-sm
                    border border-white/20">
                    <img
                      src="Profile-Ayush.jpeg"
                      alt="Ayush Rai"
                      className="w-80 h-80 sm:w-96 sm:h-96 object-cover rounded-2xl
                        transform group-hover:scale-105 transition-transform duration-500"
                    />
                    
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
                    
                    {/* Tech Stack Badges */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                      <span className="px-2 py-1 bg-black/80 backdrop-blur-sm rounded-lg text-white text-xs">React</span>
                      <span className="px-2 py-1 bg-black/80 backdrop-blur-sm rounded-lg text-white text-xs">Node.js</span>
                      <span className="px-2 py-1 bg-black/80 backdrop-blur-sm rounded-lg text-white text-xs">Next.js</span>
                    </div>
                  </div>
                  
                  {/* Floating Badge */}
                  <div className="absolute -bottom-4 -right-4 
                    bg-gradient-to-r from-[#f29f67] to-[#3b8ff3]
                    text-white rounded-xl px-5 py-3 shadow-xl
                    transform group-hover:scale-110 transition-transform duration-300
                    animate-float">
                    <div className="text-xs font-semibold uppercase tracking-wider">Full Stack</div>
                    <div className="text-lg font-bold" style={{ fontFamily: "'Syne', sans-serif" }}>Developer</div>
                  </div>
                </div>
              </Fade>
            </div>
          </div>

          {/* Stats Section */}
          <Fade bottom>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-8 border-t border-gray-200 dark:border-white/10">
              {[
                { value: "3+", label: "Internships", icon: "💼" },
                { value: "10+", label: "Projects Built", icon: "🚀" },
                { value: "150K+", label: "Users Impacted", icon: "👥" },
                { value: "100%", label: "Client Satisfaction", icon: "⭐" },
              ].map((stat, idx) => (
                <div key={idx} 
                  className="text-center group cursor-pointer
                    p-4 rounded-xl
                    hover:bg-white/50 dark:hover:bg-white/5
                    transition-all duration-300
                    hover:-translate-y-1">
                  <div className="text-3xl mb-2 group-hover:scale-110 transition-transform">
                    {stat.icon}
                  </div>
                  <div className="text-2xl font-bold text-gray-800 dark:text-white" 
                       style={{ fontFamily: "'Syne', sans-serif" }}>
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-1">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </Fade>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-gray-400 dark:border-gray-600 flex justify-center">
          <div className="w-1 h-2 bg-gray-400 dark:bg-gray-600 rounded-full mt-2 animate-scroll"></div>
        </div>
      </div>
    </section>
  );
};

export default Home;